/**
 * @file: useCachedQuery.ts
 * @copyright: (c) 2019-2021 sichuan zhichetech co., ltd.
 */

import { CachedObject } from '@euler/lib/cache/CachedObject';
import { useCreation, usePersistFn } from '@euler/utils/hooks';
import { useEffect, useState } from 'react';
import { InteractionManager } from 'react-native';

export const useCachedQuery = <T>(
  cachePath: string,
  ttl: number,
  query: (cached: T | undefined) => Promise<T | null | undefined>,
  enabled = true,
  options?: {
    initialState?: T | undefined;
    updateAfterInteractions?: boolean;
    validate?: (object: T) => boolean;
    deps?: readonly unknown[];
  },
) => {
  const [state, setState] = useState<T | undefined>(options?.initialState);
  const [loading, setLoading] = useState<boolean>(true);
  const [refreshing, setRefreshing] = useState<boolean>();
  const [error, setError] = useState<Error>();

  const cachedObject = useCreation(
    () => new CachedObject<T>(cachePath, ttl, enabled),
    [cachePath, ttl, enabled],
  );

  const fetch = usePersistFn(async (cached: T | undefined) => {
    try {
      setError(undefined);
      const res = await query(cached);
      if (res == null) {
        setState(undefined);
        void cachedObject.delete();
        return;
      }
      if (options?.updateAfterInteractions) {
        void InteractionManager.runAfterInteractions(() => {
          setState(res);
        });
      } else {
        setState(res);
      }
      void cachedObject.write(res);
    } catch (e) {
      console.error(e);
      setError(e as unknown as Error);
    }
  });

  const invalidate = usePersistFn(async (silence?: boolean) => {
    !silence && setRefreshing(true);
    await fetch(state);
    !silence && setRefreshing(false);
  });

  const isValid = usePersistFn((object: T) => {
    return Boolean(options?.validate == null || options?.validate(object));
  });

  const extraDeps = options?.deps;

  useEffect(() => {
    cachedObject
      .read()
      .then(res => {
        const object =
          res.object && isValid(res.object) ? res.object : undefined;
        if (object) {
          setState(object);
        }
        if (!object || res.expired) {
          return fetch(object);
        }
      })
      .catch(err => {
        console.error(err);
        setError(err);
      })
      .finally(() => {
        setLoading(false);
      });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [cachedObject, fetch, isValid, ...(extraDeps ?? [])]);

  const update = usePersistFn((object: T) => {
    setState(object);
    void cachedObject.write(object);
  });

  return {
    state,
    loading,
    refreshing,
    error,
    invalidate,
    update,
  };
};
